import { env } from '@shared/config/env';
import { extractApiErrorMessage } from '@entities/session';

export interface VerifyFirstLoginOtpParams {
  userId: string;
  otp: string;
}

export type VerifyFirstLoginOtpResult =
  | { ok: true; token?: string | undefined; message?: string | undefined }
  | { ok: false; message: string; network?: boolean };

/**
 * Call POST /api/v1/auth/first-login/verify-otp with user_id and otp.
 */
export async function verifyFirstLoginOtp(
  params: VerifyFirstLoginOtpParams,
): Promise<VerifyFirstLoginOtpResult> {
  const apiBase =
    env.VITE_API_BASE_URL !== ''
      ? env.VITE_API_BASE_URL
      : typeof window !== 'undefined'
      ? window.location.origin
      : '';

  try {
    const response = await fetch(`${apiBase}/api/v1/auth/first-login/verify-otp`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        user_id: params.userId,
        otp: params.otp,
      }),
    });

    const json = (await response.json().catch(() => null)) as {
      status?: boolean;
      message?: string;
      data?: {
        token?: string;
        reset_token?: string;
        access_token?: string;
      };
    } | null;

    if (!response.ok || json?.status === false) {
      const message =
        extractApiErrorMessage(json) ?? json?.message ?? 'Invalid or expired verification code.';
      return { ok: false, message };
    }

    const token = json?.data?.token ?? json?.data?.reset_token ?? json?.data?.access_token;
    return { ok: true, token, message: json?.message };
  } catch {
    return { ok: false, message: 'Unable to verify code due to a network error.', network: true };
  }
}

/**
 * Call POST /api/v1/auth/first-login/resend-otp with user_id.
 */
export async function resendFirstLoginOtp(
  userId: string,
): Promise<{ ok: boolean; message?: string | undefined }> {
  const apiBase =
    env.VITE_API_BASE_URL !== ''
      ? env.VITE_API_BASE_URL
      : typeof window !== 'undefined'
      ? window.location.origin
      : '';

  try {
    const response = await fetch(`${apiBase}/api/v1/auth/first-login/resend-otp`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });

    const json = (await response.json().catch(() => null)) as {
      status?: boolean;
      message?: string;
    } | null;

    if (response.ok && json?.status !== false) {
      return { ok: true, message: json?.message };
    }
    return {
      ok: false,
      message: extractApiErrorMessage(json) ?? 'Failed to resend verification code.',
    };
  } catch {
    return { ok: false, message: 'Unable to resend code due to a network error.' };
  }
}
